import { config } from '../config/environments';

// Demo authentication service for public safety demo
export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  displayName: string;
  department: string;
  role: 'Admin' | 'Demo';
  lastLoginDate: string;
  isActive: boolean;
}

interface DemoCredentials {
  email: string;
  password: string;
  user: AuthUser;
}

class DemoAuthService {
  private currentUser: AuthUser | null = null;
  private readonly STORAGE_KEY = 'demo_auth_user';

  // Demo users from configuration
  private readonly demoUsers: DemoCredentials[] = (config.demo?.demoUsers || []).map(
    (configUser, index) => ({
      email: configUser.email,
      password: configUser.password,
      user: {
        userId: `demo-user-${String(index + 1).padStart(3, '0')}`,
        email: configUser.email,
        name: configUser.displayName,
        displayName: configUser.displayName,
        department: configUser.role === 'Admin' ? 'Administration' : 'Public Safety',
        role: configUser.role,
        lastLoginDate: new Date().toISOString(),
        isActive: true,
      },
    })
  );

  constructor() {
    // Check for existing session
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.currentUser = JSON.parse(stored);
      }
    } catch (_error) {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private saveUserToStorage(user: AuthUser): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
    } catch (_error) {
      // Silent error handling for storage operations
    }
  }

  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Find matching user
      const credentials = this.demoUsers.find(
        (cred) => cred.email.toLowerCase() === email.toLowerCase() && cred.password === password
      );

      if (!credentials) {
        return { success: false, error: 'Invalid email or password' };
      }

      // Update last login date
      const user = {
        ...credentials.user,
        lastLoginDate: new Date().toISOString(),
      };

      this.currentUser = user;
      this.saveUserToStorage(user);

      return { success: true };
    } catch (_error) {
      return { success: false, error: 'Login failed. Please try again.' };
    }
  }

  async getToken(): Promise<string | null> {
    if (!this.currentUser) {
      return null;
    }
    // Return a demo token that includes role information
    return `demo-token-${this.currentUser.role.toLowerCase()}-${this.currentUser.userId}`;
  }

  getUser(): AuthUser | null {
    return this.currentUser;
  }

  async getUserAsync(): Promise<AuthUser | null> {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  isAdmin(): boolean {
    return this.currentUser?.role === 'Admin';
  }

  isDemoUser(): boolean {
    return this.currentUser?.role === 'Demo';
  }

  async logout(): Promise<void> {
    this.currentUser = null;
    localStorage.removeItem(this.STORAGE_KEY);

    // Force redirect to login page after logout
    setTimeout(() => {
      window.location.replace('/login');
    }, 100);
  }

  async ensureAuthenticated(): Promise<boolean> {
    return this.isAuthenticated();
  }

  async refreshAuthenticationState(): Promise<boolean> {
    return this.isAuthenticated();
  }

  // Helper method to check permissions based on configuration
  canAccessSettings(): boolean {
    if (this.isAdmin()) return true;
    return !config.demo?.restrictedFeatures.includes('settings');
  }

  canPlayAudio(): boolean {
    if (this.isAdmin()) return true;
    return !config.demo?.restrictedFeatures.includes('audioPlayback');
  }

  canUploadFiles(): boolean {
    if (this.isAdmin()) return true;
    return !config.demo?.restrictedFeatures.includes('fileUpload');
  }

  canViewCallDetails(): boolean {
    return true; // Both admin and demo users can view call details
  }

  getContactRedirectUrl(feature: string): string {
    return `${config.demo?.contactRedirectUrl || config.external.contactUrl}&feature=${feature}`;
  }
}

export const demoAuthService = new DemoAuthService();
